/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
import EmberObject, { computed, set } from '@ember/object';

import { A } from '@ember/array';
import * as ResultsUtils from '../utils/results';
import MathUtils from '../utils/math';
import CircuitResults from './circuit-results';
import VoltageRangeResults from './sub-results/voltage-range';

export default class CircuitGroupResults extends EmberObject {
  actualStages = null;
  adcBandwidth = null;
  frequencyArray = null;
  sortOrder = null;
  @computed('actualStages') get vsMinus() {
    let actualStages = this.actualStages;
    let result = actualStages[0].componentsInput.vsMinus;
    return result;
  }
  @computed('actualStages') get vsPlus() {
    let actualStages = this.actualStages;
    let result = actualStages[0].componentsInput.vsPlus;
    return result;
  }
  whoAmI = 'app/models/results/circuit-group-results.js';
  init() {
    if (this.sortOrder === null) {
      set(this, 'sortOrder', []);
    }
  }

  @computed('actualStages', 'frequencyArray') get circuits() {
    var actualStages = this.actualStages;
    var frequencyArray = this.frequencyArray;
    var result = actualStages.map((item) => {
      var circuit = item.circuit;
      set(circuit, 'frequencyArray', frequencyArray);
      return circuit;
    });
    return result;
  }

  @computed('adcBandwidth', 'circuits', 'actualStages', 'frequencyArray') get circuitResults() {
    var adcBandwidth = this.adcBandwidth;
    var circuits = this.circuits;
    var actualStages = this.actualStages;
    var frequencyArray = this.frequencyArray;
    var result = actualStages.map((item, index) => {
      let circuit = circuits[index];
      let circuitResult = CircuitResults.create({
        circuit: circuit,
        actualStage: item,
        adcBandwidth: adcBandwidth,
        frequencyArray: frequencyArray,
      });
      return circuitResult;
    });

    return result;
  }

  @computed('circuitResults', 'sortOrder') get circuitResultsSorted() {
    let sortOrder = this.sortOrder;
    let circuitResults = this.circuitResults;
    var result = circuitResults;
    if (sortOrder.length > 0) {
      result = sortOrder.map((stageId) => {
        return circuitResults.find((item) => {
          return item.actualStage.stageId === stageId;
        });
      });
    }

    return result;
  }

  @computed('circuitResultsSorted') get overallLimits() {
    var circuitResults = this.circuitResultsSorted;
    var result = {
      gbw: Math.max.apply(
        Math,
        circuitResults.map((item) => {
          return item.idealOpAmp.gbw;
        }),
      ),
      iBias: Math.min.apply(
        Math,
        circuitResults.map((item) => {
          return item.idealOpAmp.iBias;
        }),
      ),
      rOutOverGBW: Math.min.apply(
        Math,
        circuitResults.map((item) => {
          return item.idealOpAmp.rOutOverGBW;
        }),
      ),
      specialGain: Math.min.apply(
        Math,
        circuitResults.map((item) => {
          return item.idealOpAmp.specialGain;
        }),
      ),
      slewRate: Math.max.apply(
        Math,
        circuitResults.map((item) => {
          return item.idealOpAmp.slewRate;
        }),
      ),
    };

    return result;
  }

  @computed('circuitResultsSorted', 'adcBandwidth', 'frequencyArray') get lastCircuitNoiseGroup() {
    var circuitResults = this.circuitResultsSorted;
    var adcBandwidth = this.adcBandwidth;
    var previousNoiseGroup = null;
    let noiseGroups = [];
    for (let i = 0; i < circuitResults.length; i++) {
      let circuitResult = circuitResults[i];
      let circuit = circuitResult.circuit;
      let supportedComponents = circuit.supportedComponents;
      var opAmp = circuit.opAmp;
      var magnitude = circuitResult.magnitude;
      var frequencyArray = this.frequencyArray;

      var filterAmplitude = circuitResult.noiseFilterAmplitude;
      let hash = {
        r1: null,
        r2: null,
        r3: null,
        r4: null,
        r5: null,
        c1: null,
        c2: null,
        c5: null,
        opAmp: opAmp,
        magnitude: magnitude,
        frequencyArray: frequencyArray,
        adcBandwidth: adcBandwidth,
        filterAmplitude: filterAmplitude,
      };
      for (let j = 0; j < supportedComponents.length; j++) {
        let componentName = supportedComponents[j];
        let valuePath = componentName + '.value';
        let componentValue = circuit.get(valuePath);
        hash[componentName] = componentValue;
      }

      if (previousNoiseGroup) {
        hash.previousNoiseGroup = previousNoiseGroup;
      }

      let noiseGroup = circuit.noiseGroupModel.create(hash);
      previousNoiseGroup = noiseGroup;
      noiseGroups.push(noiseGroup);
    }

    return A(noiseGroups).lastObject;
  }

  @computed('circuitResults') get magnitude() {
    var circuitResults = this.circuitResults;
    var magnitudes = circuitResults.map((item) => {
      return item.magnitude;
    });
    var result = ResultsUtils.getMagnitudeArray(magnitudes);
    return result;
  }

  @computed('circuitResults') get phase() {
    var circuitResults = this.circuitResults;
    var phases = circuitResults.map((item) => {
      return item.phase;
    });
    var result = ResultsUtils.getPhaseArray(phases);
    return result;
  }

  @computed('circuitResults') get stepResponse() {
    var circuitResults = this.circuitResults;
    var stepValues = circuitResults[0].stepResponse;

    if (circuitResults.length > 1) {
      for (var i = 1; i < circuitResults.length; i++) {
        var impulseValues = circuitResults[i].impulseResponse;
        stepValues = MathUtils.convolve(stepValues, impulseValues);
      }
    }

    return stepValues;
  }

  @computed('circuitResultsSorted', 'vsMinus', 'vsPlus') get maxInputSignalPossible() {
    let circuitResultsSorted = this.circuitResultsSorted;
    let vsMinus = this.vsMinus;
    let vsPlus = this.vsPlus;
    var voltageRangeResults = VoltageRangeResults.create({
      circuitResults: circuitResultsSorted,
      vsMinus: vsMinus,
      vsPlus: vsPlus,
      worstCaseMagnitudes: null,
      toleranceInput: false,
    });
    var result = voltageRangeResults.maxInputSignalPossible;
    return result;
  }
}
